import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-message-composer>` says about itself.
 *
 * It does not send. It reports that somebody pressed send, with what they wrote, and clears itself.
 * Where the message goes, whether it succeeded and what happens if it did not are the surrounding
 * application's, and a composer that decided any of them would only work for one application.
 *
 * The same is true of drafts and of files. A draft is REPORTED on every keystroke and kept nowhere;
 * a chosen file is reported and uploaded nowhere. Both are data belonging to somebody the library
 * has never met, and choosing a place to put it would be choosing on their behalf.
 *
 * Enter sends and Shift+Enter starts a line — including every other modifier, and including the
 * case where a keyboard's input method is mid-character, where Enter is choosing that character
 * rather than sending.
 *
 * The dictate control is drawn only when the browser can actually do it AND the author asked. That
 * is a deliberate exception to the library's usual habit of drawing what it is told to: a control
 * that is present and does nothing teaches people not to trust the controls.
 *
 * Mentions are the same bargain as everything else here: the box spots one being typed and says so;
 * WHO can be named is answered by the surface, through `answerMentions`. What it does own is the
 * keyboard, because focus never leaves the text box while somebody is typing a mention.
 *
 * The answer names the question it answers, and that is not ceremony. Two lookups can be in flight
 * at once and can finish in either order; an answer that cannot say which query it belongs to gets
 * shown against whichever one is current, and Enter then inserts the wrong person.
 */
const docs: ComponentDocs = {
  aliases: ['chat input', 'message box', 'comment box', 'reply box', 'send message', 'chat composer'],

  answers: [
    'let somebody write and send a message',
    'grow a text box as somebody types',
    'send on Enter and start a line on Shift+Enter',
    'offer to attach a file to a message',
    'offer to dictate a message',
    'let somebody mention a colleague while writing',
  ],

  placements: ['recordPage', 'homePage', 'appPage'],

  inputs: [
    {
      name: 'placeholder',
      attribute: 'placeholder',
      type: 'text',
      default: 'Write a message…',
      description:
        'The grey line in the empty box. Worth setting to name the conversation — "Message #deploys" ' +
        'tells somebody where what they type is about to go, which a generic line does not.',
    },
    {
      name: 'disabled',
      attribute: 'disabled',
      type: 'boolean',
      default: false,
      description:
        'Nothing can be typed or sent. For a conversation that is closed, an outage, or somebody who ' +
        'may read but not write. The box says so by being visibly unavailable rather than by refusing ' +
        'after somebody has written a message.',
    },
    {
      name: 'sendLabel',
      attribute: 'send-label',
      type: 'text',
      default: 'Send',
      description: 'What the send control says, for a surface whose word for this is not "send".',
    },
    {
      name: 'attach',
      attribute: 'attach',
      type: 'boolean',
      default: false,
      description:
        'Offer an attach control. Chosen files are REPORTED through the `attach` event and are not ' +
        'uploaded, held or attached to anything by this component — it does not know where your files ' +
        'go.',
    },
    {
      name: 'dictate',
      attribute: 'dictate',
      type: 'boolean',
      default: false,
      description:
        'Offer a dictate control, IF the browser has speech recognition. Asking for it in a browser ' +
        'that does not draws nothing rather than a control that fails when pressed. What is recognised ' +
        'is appended to what was already typed.',
    },
    {
      name: 'maxHeight',
      attribute: 'max-height',
      type: 'number',
      default: 160,
      description:
        'How tall the box may grow, in pixels, before it scrolls instead. A box with no ceiling pushes ' +
        'the conversation it belongs to off the screen.',
    },
    {
      name: 'mentions',
      attribute: 'mentions',
      type: 'boolean',
      default: false,
      description:
        'Typing "@" opens a list above the box. The box does not know who exists: it asks through the ' +
        '`mention-query` event, and the surface answers with `answerMentions`. Without this, "@" is ' +
        'an ordinary character and nothing is asked.',
    },
    {
      name: 'mentionCandidates',
      type: 'json',
      default: [],
      description:
        'The answer to the question being asked RIGHT NOW — `{ kind, ref, label, hint? }` entries, in ' +
        'the order they should read. For a surface that answers synchronously. Anything that fetches ' +
        'must call `answerMentions` instead and name the query it is answering, or a slow answer to ' +
        'an older question will be shown as the answer to the current one.',
    },
  ],

  events: [
    {
      name: 'send',
      carries: '{ body: string }',
      when:
        'Somebody pressed Enter or the send control with something other than whitespace in the box. ' +
        'The box has already cleared itself and taken focus back by the time this is heard — sending ' +
        'is the surface’s job, and if it fails the surface says so.',
    },
    {
      name: 'draft',
      carries: '{ value: string }',
      when:
        'On every keystroke, and once more with an empty value after a send. Whether a draft is stored, ' +
        'where and for how long is the surface’s decision; this component keeps nothing.',
    },
    {
      name: 'mention-query',
      carries: '{ query: string | null }',
      when:
        'The mention being typed changed — including to `null`, which means one is no longer being ' +
        'typed. Null is not "nothing matched": it is what tells the surface to abandon a lookup still ' +
        'in flight, and a surface that ignores it will answer a question nobody is asking any more. ' +
        'An empty string is a real query — a bare "@" asks who is here.',
    },
    {
      name: 'mention',
      carries: '{ kind, ref, label, hint? }',
      when:
        'Somebody chose from the list and the mention has been written into the box. Raised as well ' +
        'as `draft`, because the CHOICE is what lets a surface keep the label it has already resolved ' +
        'instead of asking again.',
    },
    {
      name: 'attach',
      carries: '{ files: File[] }',
      when:
        'Somebody chose one or more files. Nothing has been uploaded. The chooser is cleared afterwards ' +
        'so that choosing the same file twice in a row is still reported — a file input otherwise ' +
        'reports only a CHANGE, and the second attempt would be silent.',
    },
  ],

  state: [
    {
      name: 'value',
      type: 'text',
      description:
        'What is in the box. Readable and settable — setting it is how a stored draft comes back in, ' +
        'and it re-measures the box’s height.',
    },
    {
      name: 'mentionsEnabled',
      type: 'boolean',
      description: 'Whether this box is offering mentions at all.',
    },
    {
      name: 'pendingMentionQuery',
      type: 'text, or null',
      description:
        'The query this box is waiting on an answer for, or null when it is not waiting on one. What ' +
        'a surface passes back to `answerMentions`.',
    },
  ],

  slots: [],

  tokens: [
    { token: '--caos-color-text', controls: 'What is being typed, and the labels on the controls.' },
    { token: '--caos-color-text-muted', controls: 'The box when nothing may be typed into it.' },
    { token: '--caos-color-text-ghost', controls: 'A control that is not available.' },
    { token: '--caos-color-border', controls: 'The box’s edge, the controls’ edges and the rule above the whole thing.' },
    { token: '--caos-color-surface', controls: 'Behind the composer, so it reads as a band under the conversation.' },
    { token: '--caos-color-surface-2', controls: 'Inside the box and behind the controls.' },
    { token: '--caos-color-accent-fill', controls: 'The send control once there is something to send, and the dictate control while it is listening.' },
    { token: '--caos-color-accent-contrast', controls: 'The text drawn on that fill.' },
    { token: '--caos-font', controls: 'The typeface of the message and the controls.' },
    { token: '--caos-text-body', controls: 'The size of what is being typed.' },
    { token: '--caos-text-label', controls: 'The size of the control labels.' },
    { token: '--caos-space-1', controls: 'The gap between the controls.' },
    { token: '--caos-space-2', controls: 'The inside of the box, and the gap between the box and the controls.' },
    { token: '--caos-space-3', controls: 'The inset from the edges of the conversation, and how far in the mention list sits.' },
    { token: '--caos-radius-sm', controls: 'The roundness of the box and the controls.' },
    { token: '--caos-focus-ring', controls: 'The ring on the box and on each control.' },
  ],

  parts: [
    { part: 'composer', is: 'the whole band' },
    { part: 'input', is: 'the growing text box' },
    { part: 'actions', is: 'the run of controls beside it' },
    { part: 'send', is: 'the send control' },
    { part: 'attach', is: 'the attach control' },
    { part: 'dictate', is: 'the dictate control' },
    { part: 'mentions', is: 'the list of people offered while a mention is being typed', when: 'mentions are offered' },
  ],

  methods: [
    {
      name: 'submit',
      description:
        'Send what is in the box, as pressing Enter would. Returns what was sent, or null when the box ' +
        'was empty or unavailable — so a caller can tell "sent" from "there was nothing to send".',
    },
    {
      name: 'focus',
      description: 'Put the cursor in the box.',
    },
    {
      name: 'answerMentions',
      description:
        'Answer one `mention-query`, naming the query being answered. Returns whether the answer was ' +
        'used — an answer to a question nobody is asking any more is dropped. This is the path an ' +
        'asynchronous lookup must take: two lookups in flight can finish in either order, and an ' +
        'answer that cannot say which question it answers cannot be checked against the one being ' +
        'asked.',
      args: [
        { name: 'query', type: 'text', description: 'The query this answers — from the `mention-query` event.' },
        { name: 'candidates', type: 'json', description: 'The people and records that matched, in the order they should read.' },
      ],
    },
  ],

  placing:
    'Under a message stream, as the last thing in a conversation column. It draws its own rule along ' +
    'the top, so it needs nothing between it and the stream. It does not scroll with the messages.',

  whenToUse:
    'Somebody is writing prose that is SENT — a message, a reply, a comment. A value being entered on ' +
    'a record is a field, and belongs to the field renderer with everything a field brings: a label, ' +
    'validation, a save.',

  insteadUse: [
    {
      name: 'TextField',
      when:
        'The text is a field’s value. It gets a label, validation and the save behaviour of the record ' +
        'it belongs to, none of which a message has.',
    },
    {
      name: 'FieldRenderer',
      when:
        'The writing is a rich Text field on a record, where the result is stored formatting on that ' +
        'record. A message carries a small markdown and is typed, not styled.',
    },
    {
      name: 'ActionBar',
      when: 'What is wanted is the row of actions, with no text being written at all.',
    },
  ],

  accessibility:
    'While mentions are offered the box is the COMBOBOX of a combobox-and-listbox pair: it carries ' +
    '`role="combobox"`, `aria-expanded`, `aria-controls` and `aria-activedescendant`, and the list ' +
    'beside it never takes focus. That is what lets the arrows, Enter and Escape reach the list ' +
    'while every character still goes into the box. Escape closes the list without deleting what was ' +
    'typed, and does not reopen it until the caret leaves that mention. None of those attributes is ' +
    'set when mentions are not offered, so a plain composer is a plain text box. ' +
    'The box is a plain `<textarea>`, so everything a browser and an assistive technology already know ' +
    'about typing into one holds: selection, dictation at the operating-system level, a text cursor, ' +
    'and an input method mid-character. Enter is only intercepted when NO modifier is held and the ' +
    'input method is not composing, which is what stops it from stealing the key that finishes a ' +
    'character. The send control is genuinely `disabled` while there is nothing to send, so it is ' +
    'skipped rather than offered and refused, and the dictate control carries `aria-pressed` while it ' +
    'is listening. Left to the author: NAMING the box. It has a placeholder and no label, and a ' +
    'placeholder is not a name — set `aria-label` on the element, or label it from the conversation’s ' +
    'own heading.',

  examples: [
    {
      key: 'plain',
      label: 'The box under a conversation',
      summary: 'Named for the conversation it sends into.',
      placement: {
        component: 'caos-message-composer',
        inputs: { placeholder: 'Message #deploys', 'aria-label': 'Message #deploys' },
      },
    },
    {
      key: 'full',
      label: 'With attach and dictate offered',
      summary:
        'Both are opt-in. Dictate draws nothing at all in a browser without speech recognition, rather ' +
        'than drawing a control that fails when pressed.',
      placement: {
        component: 'caos-message-composer',
        inputs: { placeholder: 'Message #deploys', attach: true, dictate: true, 'aria-label': 'Message #deploys' },
      },
    },
    {
      key: 'mentions',
      label: 'With mentions offered',
      summary:
        'Typing "@" asks the surface who is here, through the `mention-query` event. This preview ' +
        'asks and is answered by nothing, so no list opens — which is also what a real surface ' +
        'shows while it is still looking. A list appears when `mentionCandidates` is set, and an ' +
        'empty list set there says "nobody by that name" rather than staying shut.',
      placement: {
        component: 'caos-message-composer',
        inputs: { placeholder: 'Message #deploys', mentions: true, 'aria-label': 'Message #deploys' },
      },
    },
    {
      key: 'closed',
      label: 'A conversation nobody may write in',
      summary: 'An outage, an archived conversation, or somebody who may read but not write.',
      placement: {
        component: 'caos-message-composer',
        inputs: { placeholder: 'This conversation is closed', disabled: true, 'aria-label': 'Closed conversation' },
      },
    },
  ],
};

export default docs;
